import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, resolveDirective as _resolveDirective, openBlock as _openBlock, createElementBlock as _createElementBlock, withDirectives as _withDirectives, withCtx as _withCtx, createTextVNode as _createTextVNode, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "cx-surfacelog-comparison-section" }
const _hoisted_2 = { class: "cx-surfacelog-comparison-section__refmodel-label" }
const _hoisted_3 = {
  key: 0,
  class: "cx-surfacelog-comparison-section__calculation-indicator"
}

import { ref, Ref, computed, ComputedRef } from 'vue';
import NCxSelect from '@/components/NCxSelect.vue';
import CxIcon from '@/components/CxIcon.vue';
import maxSize from 'popper-max-size-modifier';
import applyMaxSize from '@/components/menuComponents/popperApplyMaxSize.modifier';
import sameWidth from '@/components/menuComponents/popperSameWidth.modifier';
import { withFullPath } from '@/domain/entityMixins';
import { useStore } from 'vuex';
import { RefModel } from '@/domain/refmodels/RefModel';
import { RefModelWithFullPath } from '@/domain/refmodels/RefModelWithFullPath';
import { SurfacelogType } from '@/domain/surfacelog/SurfacelogType';
import { Surfacelog } from '@/domain/surfacelog/Surfacelog';
import CxButton from '@/components/CxButton.vue';
import { surfacelogApi } from '@/http/surfacelog.api';
import { SurfacelogSignalTypes } from '@/domain/surfacelog/SurfacelogSignalTypes.enum';
import { SurfacelogSignalSldStyle } from '@/domain/surfacelog/SurfacelogSignalSldStyle.enum';
import { SurfaceErrors } from '@/domain/surfacelog/SurfaceErrors.enum';
import { MeasurementSystem } from '@/domain/MeasurementSystem';
import { convert } from '@/utils/measurementUnitHelper';
import { gettext } from '@/translations/gettext.setup';
import { useVuex } from '@vueblocks/vue-use-vuex';
import { UseActions, UseMutations } from '@/store/UseVuex.types';


export default /*#__PURE__*/_defineComponent({
  __name: 'CxSurfaceLogComparisonSection',
  setup(__props) {

const { $gettext } = gettext;
const showRefModelTooltip = ref(false);
const loading = ref(false);
const store = useStore();

const projectUuid: ComputedRef<string> = computed(() => store.getters.projectUuid);

const refModelSelectPopperOptions: Ref<{placement: string, modifiers: any[]}> = ref({
  placement: 'bottom-start',
  modifiers: [
    maxSize,
    applyMaxSize,
    sameWidth,
    {
      name: 'flip',
      options: {
        flipVariations: false,
        fallbackPlacements: [],
      },
    },
    {
      name: 'preventOverflow',
      options: {
        altAxis: false,
      },
    },
    { name: 'offset', options: { offset: [0, 3] } }
  ],
});

const detailsSurfacelogStore = useVuex('detailsSurfacelog', store);
const {
  refModels,
  getDeltaHeightCalculation,
  isDeltaHeightCalculated,
  isDeltaHeightCalculationExpired,
  selectedRefmodelId
} = detailsSurfacelogStore.useGetters([
  'refModels',
  'getDeltaHeightCalculation',
  'isDeltaHeightCalculated',
  'isDeltaHeightCalculationExpired',
  'selectedRefmodelId'
]) as {
  refModels: Ref<RefModel[]>,
  selectedRefmodelId: Ref<(surfaceId: string) => string | null>
  getDeltaHeightCalculation: Ref<(refModelId: string, surfaceId: string) => { wmsUrl: string, signals: string[], expirationDate: Date } | null>
  isDeltaHeightCalculated: Ref<(refModelId: string, surfaceId: string) => boolean>,
  isDeltaHeightCalculationExpired: Ref<(refModelId: string, surfaceId: string) => boolean>
};

const mapStore = useVuex('map', store);

const {
  selectedEntity,
} = mapStore.useGetters([
  'selectedEntity'
]) as {
  selectedEntity: Ref<Surfacelog>,
};

const appStore = useVuex('app', store);
const { measurementSystem } = appStore.useGetters(['measurementSystem']) as { measurementSystem: Ref<MeasurementSystem> };

const {
  setSelectedRefmodelId,
} = detailsSurfacelogStore.useActions([
  'setSelectedRefmodelId'
]) as UseActions;

const {
  changeSurfaceLogSignal,
} = mapStore.useActions([
  'changeSurfaceLogSignal'
]) as UseActions;

const translations = computed(() => {
  return {
    refModelTooltip: $gettext('Select reference model to calculate height difference'),
    referenceModelsPlaceholder: $gettext('Select Reference Model'),
    refModelLabel: $gettext('Reference Model'),
    calculateButton: $gettext('Calculate dH'),
    errorMessage: $gettext('An error occured during dH calculation, please try again later'),
    calculationIndicator: $gettext('Height difference is calculated'),
    rasterSizeError: $gettext('Output raster size is too big. Please narrow your search criteria.'),
    tooMuchDataError: $gettext('To much data to process. Please narrow your search criteria.'),
    refmodelRasterizationError: $gettext('Calculation of delta height failed, because of invalid reference model'),
    noRastersIntersection: $gettext('Reference model and visualized surface does not overlap, no comparison calculation can be done')
  };
});

const refModelsToSelect = computed(() => {
  const comparableRefmodelExtensions = [
    'trm',
    'lmd',
    'xml',
    'cfm',
    'dxf'
  ];
  const comparableRefmodels = refModels.value.filter((refmodel) => {
    const endOfRefModelName = refmodel.name.split('.').at(-1);

    return endOfRefModelName && comparableRefmodelExtensions.includes(
      endOfRefModelName.toLowerCase()
    );
  }).sort((a, b) => a.name.localeCompare(b.name));
  return comparableRefmodels.map(withFullPath).map((refmodel: RefModelWithFullPath) => ({
    id: refmodel.uuid,
    title: refmodel.name,
    subtitle: refmodel.fullPath
  }));
});

const selectedSurfaceLog = computed(() => {
  if (selectedEntity.value && selectedEntity.value.entityType === SurfacelogType) {
    return selectedEntity.value;
  }
  return null;
});

// It returns an Array because of selectedItemIds property in NCxSelect component
const selectedRefmodelIds = computed(() => {
  return selectedRefmodelId.value(selectedSurfaceLog.value!.uuid) ? [selectedRefmodelId.value(selectedSurfaceLog.value!.uuid)] : [];
});

const isCalculateButtonDisabled = computed(() => {
  const refmodelId = selectedRefmodelId.value(selectedSurfaceLog.value!.uuid);
  return !(refmodelId) || isDeltaHeightCalculated.value(refmodelId, selectedSurfaceLog.value!.uuid);
});

const isCalculationIndicatorVisible = computed(() => {
  const refmodelId = selectedRefmodelId.value(selectedSurfaceLog.value!.uuid);
  if (refmodelId) {
    return isDeltaHeightCalculated.value(refmodelId, selectedSurfaceLog.value!.uuid);
  }
  return false;
});

const notificationsStore = useVuex('notifications', store);

const { error, warning } = notificationsStore.useActions(['error', 'warning']) as UseActions;
const { updateSurfacelog } = mapStore.useMutations(['updateSurfacelog']) as UseMutations;
const { setDeltaHeightCalculation } = detailsSurfacelogStore.useMutations(['setDeltaHeightCalculation']) as UseMutations;

const toggleSurfacelogSignal = async () => {
  if (selectedSurfaceLog.value) {
    await changeSurfaceLogSignal(
      { uuid: selectedSurfaceLog.value.uuid,
        signal: SurfacelogSignalTypes.DeltaHeight,
        styleSLD: SurfacelogSignalSldStyle.DH,
        isOutsideRefModelVisible: selectedEntity.value.isOutsideRefModel,
        deltaHeightLegend: selectedEntity.value.deltaHeightLegend }
    );
    selectedSurfaceLog.value.signal = SurfacelogSignalTypes.DeltaHeight;
  }
};

const calculateDeltaHeight = async () => {
  loading.value = true;
  const refModel = refModels.value.find(e => e.uuid === selectedRefmodelId.value(selectedSurfaceLog.value!.uuid));
  if (selectedSurfaceLog.value && refModel) {
    try {
      const { startDate, endDate, units, cellSize, pointCodes } = selectedSurfaceLog.value;
      const refModelPaths = selectedSurfaceLog.value.refmodelPaths;
      const targetReferenceModelFullPath = `${refModel.path}/${refModel.name}`;
      const convertedCellSize = convert(cellSize)
        .withPrecision(2)
        .from(measurementSystem.value.baseLengthUnit.name)
        .to('m');

      const result = await surfacelogApi.getSurfacelogVisualization(projectUuid.value, startDate, endDate, refModelPaths, pointCodes, convertedCellSize, units, targetReferenceModelFullPath);
      updateSurfacelog({ surfacelogId: selectedSurfaceLog.value.uuid, wmsUrl: result.url, signals: result.signals });
      setDeltaHeightCalculation({ refModelId: selectedRefmodelId.value(selectedSurfaceLog.value.uuid),
        surfaceId: selectedSurfaceLog.value.uuid,
        wmsUrl: result.url,
        signals: result.signals });
      await toggleSurfacelogSignal();
    } catch (err: any) {
      const errorCode = err.response?.data?.detail;
      if (errorCode === SurfaceErrors.RasterTooBig) {
        error({ exception: err, message: translations.value.rasterSizeError });
      } else if (errorCode === SurfaceErrors.TooMuchData) {
        error({ exception: err, message: translations.value.tooMuchDataError });
      } else if (errorCode === SurfaceErrors.VisualizationFailed) {
        error({ exception: err, message: translations.value.errorMessage });
      } else if (errorCode === SurfaceErrors.RefmodelRasterizationFailed) {
        error({ exception: err, message: translations.value.refmodelRasterizationError });
      } else if (errorCode === SurfaceErrors.NoRastersIntersection) {
        warning({ exception: err, message: translations.value.noRastersIntersection });
      } else {
        error({ exception: err, message: translations.value.errorMessage });
      }
    } finally {
      loading.value = false;
    }
  }
  loading.value = false;
};

const handleSelectedItem = async (items: string[]) => {
  setSelectedRefmodelId({ surfaceId: selectedSurfaceLog.value!.uuid, refModelId: items[0] });
  if (isDeltaHeightCalculationExpired.value(items[0], selectedSurfaceLog.value!.uuid)) {
    await calculateDeltaHeight();
  }
  if (isDeltaHeightCalculated.value(items[0], selectedSurfaceLog.value!.uuid)) {
    const selectedDeltaHeightCalculation = getDeltaHeightCalculation.value(items[0], selectedSurfaceLog.value!.uuid);
    if (selectedDeltaHeightCalculation && (selectedSurfaceLog.value!.wmsUrl !== selectedDeltaHeightCalculation.wmsUrl)) {
      updateSurfacelog({ surfacelogId: selectedSurfaceLog.value!.uuid, wmsUrl: selectedDeltaHeightCalculation.wmsUrl, signals: selectedDeltaHeightCalculation.signals });
      await toggleSurfacelogSignal();
    }
  }
};


return (_ctx: any,_cache: any) => {
  const _directive_cx_tooltip = _resolveDirective("cx-tooltip")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(NCxSelect, {
      items: refModelsToSelect.value,
      selectedItemIds: selectedRefmodelIds.value,
      "onUpdate:selectedItemIds": handleSelectedItem,
      activatorPlaceholder: translations.value.referenceModelsPlaceholder,
      popperOptions: refModelSelectPopperOptions.value,
      disabled: loading.value
    }, {
      label: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("span", null, _toDisplayString(translations.value.refModelLabel), 1),
          _withDirectives((_openBlock(), _createElementBlock("div", {
            onMouseenter: _cache[0] || (_cache[0] = ($event: any) => (showRefModelTooltip.value = true)),
            onMouseleave: _cache[1] || (_cache[1] = ($event: any) => (showRefModelTooltip.value = false))
          }, [
            _createVNode(CxIcon, {
              icon: "mdi-information",
              class: "cxu-pr-5"
            })
          ], 32)), [
            [_directive_cx_tooltip, {
              text: translations.value.refModelTooltip,
              show: showRefModelTooltip.value,
              position: 'top-left',
              maxWidth: 400,
            }]
          ])
        ])
      ]),
      _: 1
    }, 8, ["items", "selectedItemIds", "activatorPlaceholder", "popperOptions", "disabled"]),
    _createVNode(CxButton, {
      disabled: isCalculateButtonDisabled.value,
      loading: loading.value,
      onClick: calculateDeltaHeight,
      variantPrimary: true
    }, {
      default: _withCtx(() => [
        _createTextVNode(_toDisplayString(translations.value.calculateButton), 1)
      ]),
      _: 1
    }, 8, ["disabled", "loading"]),
    (isCalculationIndicatorVisible.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createVNode(CxIcon, {
            icon: "done",
            size: 14
          }),
          _createElementVNode("span", null, _toDisplayString(translations.value.calculationIndicator), 1)
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})